/**
 * Permission utility functions for role-based access control
 *
 * Supports both role-based and permission-based navigation.
 * Staff members use permission-based access (their permissions are resolved
 * from role + branch restrictions on the backend).
 */

export type UserRole =
  | "SUPER_ADMIN"
  | "TENANT_ADMIN"
  | "LIBRARIAN"
  | "STAFF"
  | "STUDENT"
  | "PATRON";

export interface NavItem {
  id: string;
  label: string;
  icon: string; // lucide-react icon name
  path?: string; // Optional if has submenu
  /** When true, item is only active for exact path match (avoids parent matching child routes) */
  exact?: boolean;
  requiredPermissions?: string[];
  requiredRole?: UserRole[];
  /** Child nav items; may nest recursively (any depth). Parent rows without `path` act as expandable groups. */
  submenu?: NavItem[];
}

/** Platform super-admin: explicit flag from /auth/me or wildcard `*` in permissions. */
export function isGlobalSuperAdmin(
  user: { isSuperAdmin?: boolean; permissions?: string[] } | null | undefined
): boolean {
  if (!user) return false;
  return Boolean(user.isSuperAdmin) || Boolean(user.permissions?.includes("*"));
}

/** Resolve sidebar / documentation role from auth user (matches AppLayout logic). */
export function resolveAppUserRole(
  user:
    | {
        isStaff?: boolean;
        roleName?: string;
        isSuperAdmin?: boolean;
        permissions?: string[];
        memberships?: Array<{ roleName?: string; role?: string }>;
      }
    | null
    | undefined
): UserRole | undefined {
  if (!user) return undefined;
  if (isGlobalSuperAdmin(user)) return "SUPER_ADMIN";

  if (user.isStaff) {
    const staffRoleName = user.roleName?.toUpperCase()?.replace(/\s+/g, "_");
    if (staffRoleName?.includes("TENANT") && staffRoleName?.includes("ADMIN")) {
      return "TENANT_ADMIN";
    }
    if (staffRoleName === "LIBRARIAN") return "LIBRARIAN";
    return "STAFF";
  }

  const activeMembership = user.memberships?.[0];
  if (!activeMembership) return undefined;

  const roleName = activeMembership.roleName?.toUpperCase() || activeMembership.role?.toUpperCase();
  if (roleName === "TENANT ADMIN" || roleName === "TENANT_ADMIN") return "TENANT_ADMIN";
  if (roleName === "LIBRARIAN") return "LIBRARIAN";
  if (roleName === "STUDENT" || roleName === "PATRON") return "PATRON";

  return undefined;
}

/**
 * Check if user has permission to view a section
 * Supports wildcard permissions (e.g., "catalog.*" matches "catalog.view")
 */
export function canView(section: string, permissions: string[]): boolean {
  if (!permissions || permissions.length === 0) return false;

  // Check exact match
  if (permissions.includes(section)) return true;

  // Check wildcard permissions
  const sectionParts = section.split(".");
  for (const perm of permissions) {
    if (perm === "*") return true; // Global admin

    const permParts = perm.split(".");
    if (permParts.length === sectionParts.length) {
      let matches = true;
      for (let i = 0; i < permParts.length; i++) {
        if (permParts[i] !== "*" && permParts[i] !== sectionParts[i]) {
          matches = false;
          break;
        }
      }
      if (matches) return true;
    }

    // Check if permission is a parent wildcard (e.g., "catalog.*" matches "catalog.view")
    if (perm.endsWith(".*")) {
      const permPrefix = perm.slice(0, -2);
      if (section.startsWith(permPrefix + ".")) return true;
    }
  }

  return false;
}

/**
 * Check if user has any of the specified permissions
 */
export function hasAnyPermission(permissions: string[], required: string[]): boolean {
  return required.some((perm) => canView(perm, permissions));
}

/**
 * Filter navigation items based on user permissions and role
 */
export function filterNavItems(
  navItems: NavItem[],
  permissions: string[],
  userRole?: UserRole,
  isStaff?: boolean
): NavItem[] {
  return navItems
    .filter((item) => {
      // Check role requirement
      if (item.requiredRole && userRole) {
        // Staff members should see items that match their specific role
        // or items available to "STAFF" (generic staff role)
        if (!item.requiredRole.includes(userRole)) {
          // For staff, also check if item is available to any staff-level role
          if (isStaff && item.requiredRole.includes("STAFF")) {
            // Continue to permission check
          } else {
            return false;
          }
        }
      }

      // Check permission requirement
      if (item.requiredPermissions && item.requiredPermissions.length > 0) {
        // Super Admin has all permissions
        if (userRole === "SUPER_ADMIN") {
          return true;
        }

        // For Tenant Admin, show items if they have the required role AND permission
        if (userRole === "TENANT_ADMIN" && item.requiredRole?.includes("TENANT_ADMIN")) {
          const hasPermission = item.requiredPermissions.some((perm) => canView(perm, permissions));
          if (permissions.length > 0) {
            return hasPermission;
          }
          return true;
        }

        // For staff and other roles, check permissions
        return item.requiredPermissions.some((perm) => canView(perm, permissions));
      }

      // If no requirements, show item
      return true;
    })
    .map((item) => {
      // Filter submenu items if they exist
      if (item.submenu && item.submenu.length > 0) {
        const filteredSubmenu = filterNavItems(item.submenu, permissions, userRole, isStaff);
        // Only include parent item if it has at least one visible submenu item or has its own path
        if (filteredSubmenu.length > 0 || item.path) {
          return {
            ...item,
            submenu: filteredSubmenu,
          };
        }
        return null;
      }
      return item;
    })
    .filter((item): item is NavItem => item !== null);
}

/**
 * Get navigation items based on user role and permissions
 *
 * Navigation is now primarily permission-driven for staff members.
 * Staff see nav items based on their resolved permissions (role + branch).
 */
export function getNavItemsByRole(
  role: UserRole | undefined,
  permissions: string[] = [],
  isStaff: boolean = false
): NavItem[] {
  const allNavItems: NavItem[] = [
    // =========================================================================
    // SUPER ADMIN ITEMS
    // =========================================================================
    {
      id: "manage-tenants",
      label: "Manage Tenants",
      icon: "Building2",
      requiredRole: ["SUPER_ADMIN"],
      submenu: [
        {
          id: "tenants-list",
          label: "All Tenants",
          icon: "List",
          path: "/tenants",
          requiredRole: ["SUPER_ADMIN"],
        },
        {
          id: "billing-plans",
          label: "Billing Plans",
          icon: "CreditCard",
          path: "/billing-plans",
          requiredRole: ["SUPER_ADMIN"],
        },
      ],
    },
    {
      id: "sa-analytics",
      label: "Analytics",
      icon: "BarChart3",
      path: "/analytics",
      requiredRole: ["SUPER_ADMIN"],
    },
    {
      id: "sa-settings",
      label: "Settings",
      icon: "Settings",
      path: "/settings",
      requiredRole: ["SUPER_ADMIN"],
    },

    // =========================================================================
    // DASHBOARD - Always visible for admin/staff roles
    // Tenant Admin dashboard is unconditional (core feature, not permission-gated)
    // Staff/Librarian dashboard requires dashboard.view permission
    // =========================================================================
    {
      id: "dashboard",
      label: "Dashboard",
      icon: "LayoutDashboard",
      path: "/dashboard",
      exact: true,
      requiredRole: ["TENANT_ADMIN"],
    },
    {
      id: "staff-dashboard",
      label: "Dashboard",
      icon: "LayoutDashboard",
      path: "/dashboard",
      exact: true,
      requiredRole: ["LIBRARIAN", "STAFF"],
      requiredPermissions: ["dashboard.view"],
    },

    // =========================================================================
    // CATALOG MANAGEMENT
    // =========================================================================
    {
      id: "catalog-management",
      label: "Catalog",
      icon: "Book",
      requiredRole: ["TENANT_ADMIN", "LIBRARIAN", "STAFF"],
      requiredPermissions: ["catalog.view"],
      submenu: [
        {
          id: "catalog-all",
          label: "All Resources",
          icon: "List",
          path: "/catalog",
          requiredRole: ["TENANT_ADMIN", "LIBRARIAN", "STAFF"],
          requiredPermissions: ["catalog.view"],
        },
        {
          id: "catalog-acquisition-cataloging",
          label: "Acquisition cataloging",
          icon: "ClipboardList",
          path: "/catalog/acquisition-cataloging",
          requiredRole: ["TENANT_ADMIN", "LIBRARIAN", "STAFF"],
          requiredPermissions: ["catalog.view"],
        },
        {
          id: "upload-eresource",
          label: "Upload E-Resource",
          icon: "Upload",
          path: "/catalog/upload",
          requiredRole: ["TENANT_ADMIN", "LIBRARIAN", "STAFF"],
          requiredPermissions: ["catalog.create"],
        },
        {
          id: "paid-digital-hub",
          label: "Paid Digital Access",
          icon: "Receipt",
          requiredRole: ["TENANT_ADMIN", "LIBRARIAN", "STAFF"],
          requiredPermissions: ["digital.paidAccess.manage", "digital.*"],
          submenu: [
            {
              id: "paid-resource-purchases",
              label: "Purchases",
              icon: "Receipt",
              path: "/catalog/paid-purchases",
              requiredRole: ["TENANT_ADMIN", "LIBRARIAN", "STAFF"],
              requiredPermissions: ["digital.paidAccess.manage", "digital.*"],
            },
            {
              id: "paid-resource-entitlements",
              label: "Entitlements & revoke",
              icon: "Shield",
              path: "/catalog/paid-entitlements",
              requiredRole: ["TENANT_ADMIN", "LIBRARIAN", "STAFF"],
              requiredPermissions: ["digital.paidAccess.manage", "digital.*"],
            },
          ],
        },
        {
          id: "isbn-catalog",
          label: "ISBN Auto-Catalog",
          icon: "Scan",
          path: "/catalog/isbn",
          requiredRole: ["TENANT_ADMIN", "LIBRARIAN", "STAFF"],
          requiredPermissions: ["catalog.create"],
        },
        {
          id: "create-journal",
          label: "Create Journal",
          icon: "BookOpen",
          path: "/catalog/journal",
          requiredRole: ["TENANT_ADMIN", "LIBRARIAN", "STAFF"],
          requiredPermissions: ["catalog.create"],
        },
        {
          id: "bulk-import",
          label: "Bulk Import",
          icon: "Upload",
          path: "/catalog/bulk-import",
          requiredRole: ["TENANT_ADMIN", "LIBRARIAN", "STAFF"],
          requiredPermissions: ["catalog.bulkImport"],
        },
        {
          id: "bulk-export",
          label: "Bulk Export",
          icon: "Download",
          path: "/catalog/bulk-export",
          requiredRole: ["TENANT_ADMIN", "LIBRARIAN", "STAFF"],
          requiredPermissions: ["catalog.bulkExport"],
        },
        {
          id: "my-catalog-stats",
          label: "My Catalog Stats",
          icon: "BarChart3",
          path: "/staff/catalog/my-stats",
          requiredRole: ["TENANT_ADMIN", "LIBRARIAN", "STAFF"],
          requiredPermissions: ["catalog.create", "catalog.edit", "catalog.view"],
        },
        {
          id: "catalog-drafts",
          label: "My Drafts",
          icon: "FileEdit",
          path: "/staff/catalog/drafts",
          requiredRole: ["TENANT_ADMIN", "LIBRARIAN", "STAFF"],
          requiredPermissions: ["catalog.create", "catalog.edit"],
        },
        {
          id: "catalog-review-queue",
          label: "Review Queue",
          icon: "ClipboardCheck",
          path: "/staff/catalog/review-queue",
          requiredRole: ["TENANT_ADMIN", "LIBRARIAN", "STAFF"],
          requiredPermissions: ["catalog.review"],
        },
      ],
    },

    // =========================================================================
    // CIRCULATION
    // =========================================================================
    {
      id: "circulation",
      label: "Circulation",
      icon: "ArrowLeftRight",
      requiredRole: ["TENANT_ADMIN", "LIBRARIAN", "STAFF"],
      requiredPermissions: [
        "circulation.checkOut",
        "circulation.checkIn",
        "circulation.borrow",
        "circulation.return",
        "circulation.renew",
      ],
      submenu: [
        {
          id: "circulation-desk",
          label: "Circulation Desk",
          icon: "ArrowLeftRight",
          path: "/circulation",
          requiredRole: ["TENANT_ADMIN", "LIBRARIAN", "STAFF"],
          requiredPermissions: [
            "circulation.checkOut",
            "circulation.checkIn",
            "circulation.borrow",
            "circulation.return",
          ],
        },
        {
          id: "holds-reserves",
          label: "Holds & Reserves",
          icon: "Clock",
          path: "/circulation/holds",
          requiredRole: ["TENANT_ADMIN", "LIBRARIAN", "STAFF"],
          requiredPermissions: ["circulation.manageHolds"],
        },
        {
          id: "overdue-management",
          label: "Overdue Management",
          icon: "AlertCircle",
          path: "/circulation/overdue",
          requiredRole: ["TENANT_ADMIN", "LIBRARIAN", "STAFF"],
          requiredPermissions: ["circulation.overdueManagement"],
        },
        {
          id: "circulation-history",
          label: "Circulation History",
          icon: "History",
          path: "/circulation/history",
          requiredRole: ["TENANT_ADMIN", "LIBRARIAN", "STAFF"],
          requiredPermissions: ["circulation.viewHistory"],
        },
      ],
    },

    // =========================================================================
    // PATRON MANAGEMENT
    // =========================================================================
    {
      id: "patron-management",
      label: "Patron Management",
      icon: "UserCheck",
      requiredRole: ["TENANT_ADMIN", "LIBRARIAN", "STAFF"],
      requiredPermissions: ["patron.view"],
      submenu: [
        {
          id: "patrons",
          label: "Patrons",
          icon: "Users",
          path: "/patrons",
          requiredRole: ["TENANT_ADMIN", "LIBRARIAN", "STAFF"],
          requiredPermissions: ["patron.list", "patron.view"],
        },
        {
          id: "registration-approval",
          label: "Registration Approval",
          icon: "UserCheck",
          path: "/admin/registrations",
          requiredRole: ["TENANT_ADMIN", "LIBRARIAN", "STAFF"],
          requiredPermissions: ["registration.approve"],
        },
        {
          id: "card-processing",
          label: "Card Processing",
          icon: "CreditCard",
          path: "/admin/cards",
          requiredRole: ["TENANT_ADMIN", "LIBRARIAN", "STAFF"],
          requiredPermissions: ["cards.process", "cards.markPickup"],
        },
        {
          id: "user-services-guide",
          label: "User services guide",
          icon: "BookOpen",
          path: "/internal/user-services-guide",
          requiredRole: ["TENANT_ADMIN", "LIBRARIAN", "STAFF"],
          requiredPermissions: ["patron.view"],
        },
      ],
    },

    // =========================================================================
    // ACQUISITIONS
    // =========================================================================
    {
      id: "acquisitions",
      label: "Acquisitions",
      icon: "Package",
      requiredRole: ["TENANT_ADMIN", "STAFF"],
      requiredPermissions: ["acquisitions.view"],
      submenu: [
        {
          id: "acquisitions-requests",
          label: "Purchase Requests",
          icon: "FileText",
          path: "/acquisitions/requests",
          requiredRole: ["TENANT_ADMIN", "STAFF"],
          requiredPermissions: ["acquisitions.view"],
        },
        {
          id: "acquisitions-reports",
          label: "Insights",
          icon: "BarChart3",
          path: "/acquisitions/reports",
          requiredRole: ["TENANT_ADMIN", "STAFF"],
          requiredPermissions: ["acquisitions.view"],
        },
        {
          id: "acquisitions-orders",
          label: "Orders",
          icon: "ShoppingCart",
          path: "/acquisitions/orders",
          requiredRole: ["TENANT_ADMIN", "STAFF"],
          requiredPermissions: ["acquisitions.createOrder"],
        },
        {
          id: "acquisitions-receiving",
          label: "Receiving",
          icon: "PackageCheck",
          path: "/acquisitions/receiving",
          requiredRole: ["TENANT_ADMIN", "STAFF"],
          requiredPermissions: ["acquisitions.receiveItems"],
        },
        {
          id: "acquisitions-invoices",
          label: "Invoices",
          icon: "Receipt",
          path: "/acquisitions/invoices",
          requiredRole: ["TENANT_ADMIN", "STAFF"],
          requiredPermissions: ["acquisitions.view"],
        },
        {
          id: "acquisitions-vendors",
          label: "Vendors",
          icon: "Store",
          path: "/acquisitions/vendors",
          requiredRole: ["TENANT_ADMIN", "STAFF"],
          requiredPermissions: ["acquisitions.manageVendors"],
        },
        {
          id: "acquisitions-budget",
          label: "Budget",
          icon: "Wallet",
          path: "/acquisitions/budget",
          requiredRole: ["TENANT_ADMIN", "STAFF"],
          requiredPermissions: ["acquisitions.manageBudget"],
        },
      ],
    },

    // =========================================================================
    // BINDERY & REPAIR
    // =========================================================================
    {
      id: "bindery",
      label: "Bindery & Repair",
      icon: "Wrench",
      requiredRole: ["TENANT_ADMIN", "LIBRARIAN", "STAFF"],
      requiredPermissions: ["bindery.view"],
      submenu: [
        {
          id: "bindery-dashboard",
          label: "Dashboard",
          icon: "LayoutDashboard",
          path: "/bindery",
          exact: true,
          requiredRole: ["TENANT_ADMIN", "LIBRARIAN", "STAFF"],
          requiredPermissions: ["bindery.view"],
        },
        {
          id: "bindery-requests",
          label: "Requests",
          icon: "ClipboardList",
          path: "/bindery/requests",
          requiredRole: ["TENANT_ADMIN", "LIBRARIAN", "STAFF"],
          requiredPermissions: ["bindery.view", "bindery.createRequest"],
        },
        {
          id: "bindery-vendors",
          label: "Vendors",
          icon: "Store",
          path: "/bindery/vendors",
          requiredRole: ["TENANT_ADMIN", "LIBRARIAN", "STAFF"],
          requiredPermissions: ["bindery.manageVendors", "bindery.view"],
        },
        {
          id: "bindery-history",
          label: "Reports",
          icon: "BarChart3",
          path: "/bindery/history",
          requiredRole: ["TENANT_ADMIN", "LIBRARIAN", "STAFF"],
          requiredPermissions: ["bindery.view"],
        },
      ],
    },
    {
      id: "thesis",
      label: "Thesis Print & Bind",
      icon: "GraduationCap",
      requiredRole: ["TENANT_ADMIN", "LIBRARIAN", "STAFF"],
      requiredPermissions: ["thesis.view", "thesis.manage"],
      submenu: [
        {
          id: "thesis-queue",
          label: "Requests",
          icon: "List",
          path: "/bindery/thesis",
          requiredRole: ["TENANT_ADMIN", "LIBRARIAN", "STAFF"],
          requiredPermissions: ["thesis.view", "thesis.manage"],
        },
        {
          id: "thesis-pricing",
          label: "Pricing Rules",
          icon: "DollarSign",
          path: "/bindery/thesis/settings/pricing",
          requiredRole: ["TENANT_ADMIN", "LIBRARIAN", "STAFF"],
          requiredPermissions: ["thesis.manage"],
        },
      ],
    },

    // =========================================================================
    // FINES & FEES
    // =========================================================================
    {
      id: "fines",
      label: "Fines & Fees",
      icon: "DollarSign",
      requiredRole: ["TENANT_ADMIN", "LIBRARIAN", "STAFF"],
      requiredPermissions: ["fines.view"],
      path: "/fines",
    },
    {
      id: "manage-invoices",
      label: "Manage Invoices",
      icon: "FileText",
      requiredRole: ["TENANT_ADMIN", "LIBRARIAN", "STAFF"],
      requiredPermissions: ["invoices.view"],
      path: "/admin/invoices",
    },

    // =========================================================================
    // NOTIFICATIONS (in-app inbox for staff / librarians — same route as patrons)
    // =========================================================================
    {
      id: "notifications-management",
      label: "Notifications",
      icon: "Bell",
      requiredRole: ["TENANT_ADMIN", "LIBRARIAN", "STAFF"],
      requiredPermissions: [
        "notifications.view",
        "notifications.send",
        "cards.process",
        "cards.view",
      ],
      path: "/notifications",
      exact: true,
    },

    // =========================================================================
    // SUPPORT / HELP DESK (staff inbox)
    // =========================================================================
    {
      id: "support-inbox",
      label: "Support Inbox",
      icon: "LifeBuoy",
      path: "/admin/support",
      requiredRole: ["TENANT_ADMIN", "LIBRARIAN", "STAFF"],
      requiredPermissions: ["support.view"],
    },

    // =========================================================================
    // REPORTS & ANALYTICS
    // =========================================================================
    {
      id: "reports",
      label: "Reports",
      icon: "FileText",
      requiredRole: ["TENANT_ADMIN", "LIBRARIAN", "STAFF"],
      requiredPermissions: [
        "reports.circulation",
        "reports.viewPatronLogs",
        "reports.collection",
        "reports.cataloging",
      ],
      submenu: [
        {
          id: "cataloging-productivity",
          label: "Cataloging Productivity",
          icon: "BarChart3",
          path: "/admin/reports/cataloging",
          requiredRole: ["TENANT_ADMIN", "LIBRARIAN", "STAFF"],
          requiredPermissions: ["reports.cataloging"],
        },
      ],
    },

    // =========================================================================
    // ADMINISTRATION (Tenant Admin / Staff with admin permissions)
    // =========================================================================
    {
      id: "staff-management",
      label: "Staff Management",
      icon: "Users",
      requiredRole: ["TENANT_ADMIN"],
      requiredPermissions: ["staff.view"],
      submenu: [
        {
          id: "staff",
          label: "Staff",
          icon: "Users",
          path: "/setup/staff",
          requiredRole: ["TENANT_ADMIN"],
          requiredPermissions: ["staff.view"],
        },
        {
          id: "departments",
          label: "Departments",
          icon: "Building2",
          path: "/setup/departments",
          requiredRole: ["TENANT_ADMIN"],
          requiredPermissions: ["staff.view"],
        },
        {
          id: "job-titles",
          label: "Job Titles",
          icon: "Briefcase",
          path: "/setup/job-titles",
          requiredRole: ["TENANT_ADMIN"],
          requiredPermissions: ["staff.view"],
        },
      ],
    },
    {
      id: "branch-management",
      label: "Branch Management",
      icon: "Building2",
      requiredRole: ["TENANT_ADMIN"],
      requiredPermissions: ["branch.view"],
      submenu: [
        {
          id: "branches-list",
          label: "All Branches",
          icon: "MapPin",
          path: "/setup/branches",
          requiredRole: ["TENANT_ADMIN"],
          requiredPermissions: ["branch.view"],
        },
        {
          id: "item-transfers",
          label: "Item Transfers",
          icon: "ArrowLeftRight",
          path: "/setup/transfers",
          requiredRole: ["TENANT_ADMIN"],
          requiredPermissions: ["branch.view"],
        },
      ],
    },
    {
      id: "roles",
      label: "Roles & Permissions",
      icon: "Shield",
      path: "/setup/roles",
      requiredRole: ["TENANT_ADMIN"],
      requiredPermissions: ["roles.view"],
    },
    {
      id: "patron-types",
      label: "Patron Types",
      icon: "Tag",
      path: "/setup/patron-types",
      requiredRole: ["TENANT_ADMIN"],
      requiredPermissions: ["patronTypes.view"],
    },
    {
      id: "tenant-setup",
      label: "Settings",
      icon: "Settings",
      path: "/setup",
      requiredRole: ["TENANT_ADMIN"],
      requiredPermissions: ["settings.manage"],
    },
    {
      id: "security",
      label: "Security & Audit",
      icon: "ShieldCheck",
      requiredRole: ["TENANT_ADMIN"],
      requiredPermissions: ["security.viewActivityLogs"],
      path: "/setup/security",
    },

    // =========================================================================
    // STAFF SELF-SERVICE ITEMS
    // =========================================================================
    {
      id: "staff-profile",
      label: "My Profile",
      icon: "UserCircle",
      path: "/profile",
      requiredRole: ["TENANT_ADMIN", "LIBRARIAN", "STAFF"],
    },

    // =========================================================================
    // PATRON/STUDENT SELF-SERVICE ITEMS
    // =========================================================================
    {
      id: "patron-dashboard",
      label: "Dashboard",
      icon: "LayoutDashboard",
      path: "/dashboard",
      exact: true,
      requiredRole: ["PATRON", "STUDENT"],
    },
    {
      id: "catalog",
      label: "Catalog",
      icon: "Library",
      path: "/catalog",
      requiredRole: ["PATRON", "STUDENT"],
    },
    {
      id: "patron-recommend",
      label: "Recommend a title",
      icon: "BookPlus",
      path: "/recommend",
      requiredRole: ["PATRON", "STUDENT"],
    },
    {
      id: "my-books",
      label: "My Books",
      icon: "BookOpen",
      path: "/my-books",
      requiredRole: ["PATRON", "STUDENT"],
    },
    {
      id: "my-holds",
      label: "My Holds",
      icon: "Clock",
      path: "/my-holds",
      requiredRole: ["PATRON", "STUDENT"],
    },
    {
      id: "invoices",
      label: "My Invoices",
      icon: "FileText",
      path: "/invoices",
      requiredRole: ["PATRON", "STUDENT"],
    },
    {
      id: "my-fines",
      label: "My Fines",
      icon: "DollarSign",
      path: "/my-fines",
      requiredRole: ["PATRON", "STUDENT"],
    },
    {
      id: "my-thesis",
      label: "Thesis Print & Bind",
      icon: "GraduationCap",
      path: "/my/thesis",
      requiredRole: ["PATRON", "STUDENT"],
    },
    {
      id: "patron-notifications",
      label: "Notifications",
      icon: "Bell",
      path: "/notifications",
      exact: true,
      requiredRole: ["PATRON", "STUDENT"],
    },
    {
      id: "patron-support",
      label: "Support",
      icon: "LifeBuoy",
      path: "/support",
      requiredRole: ["PATRON", "STUDENT"],
      requiredPermissions: ["support.viewOwn"],
    },
  ];

  return filterNavItems(allNavItems, permissions, role, isStaff);
}
